import React, { useMemo, useState } from 'react';
import { useTable, useSortBy } from 'react-table';
import DatePicker from 'react-datepicker';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './style/reportPage.scss';
import 'react-datepicker/dist/react-datepicker-min.module.css';
import useTransactionReports from '../../hooks/useTransactionReports';
import axios from '../../helpers/axios_config';

const ReportPage = () => {
    const {
        data: reportData,
        isLoading,
        error,
        totalData,
        filters,
        setSearchTerm,
        setCategory,
        setOrderType,
        setDateRange,
        getCategories,
        getOrderTypes,
    } = useTransactionReports();

    const [searchInput, setSearchInput] = useState('');
    const [selectedCategory, setSelectedCategoryLocal] = useState('all');
    const [selectedOrderType, setSelectedOrderTypeLocal] = useState('all');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // Sync selectedCategory with filter changes
    React.useEffect(() => {
        setSelectedCategoryLocal(filters.category || 'all');
    }, [filters.category]);

    // Sync selectedOrderType with filter changes
    React.useEffect(() => {
        setSelectedOrderTypeLocal(filters.order_type || 'all');
    }, [filters.order_type]);

    // Sync date range with filter changes
    React.useEffect(() => {
        if (filters.start_date) {
            setStartDate(new Date(filters.start_date));
        } else {
            setStartDate(null);
        }
        if (filters.end_date) {
            setEndDate(new Date(filters.end_date));
        } else {
            setEndDate(null);
        }
    }, [filters.start_date, filters.end_date]);

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    // Handle search
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        setSearchTerm(value);
    };

    // Handle category filter
    const handleCategoryChange = (e) => {
        const value = e.target.value;
        setCategory(value);
    };

    // Handle order type filter
    const handleOrderTypeChange = (e) => {
        const value = e.target.value;
        setOrderType(value);
    };

    // Handle date range
    const handleDateRangeChange = (start, end) => {
        setDateRange(start, end);
    };

    // Export to PDF
//     const exportToPDF = async () => {
//   try {
//     const res = await fetch(
//       "/transactions/summary/pdf",
//       {
//         method: "GET",
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "x-api-key": apiKey,
//         },
//       }
//     );

//     const blob = await res.blob();
//     const url = window.URL.createObjectURL(blob);

//     const a = document.createElement("a");
//     a.href = url;
//     a.download = "transaction-report.pdf";
//     a.click();

//     window.URL.revokeObjectURL(url);
//   } catch (err) {
//     console.error(err);
//   }
// };
const exportToPDF = async () => {
  try {
    // Ambil token dari localStorage atau dari redux jika pakai redux
    const token = localStorage.getItem("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    console.log("=== Export PDF Debug ===");
    console.log("Token:", token);
    console.log("API Key:", apiKey);
    console.log("Base URL:", process.env.REACT_APP_API_URL);

    if (!token) {
      alert("Token tidak ditemukan. Silakan login terlebih dahulu.");
      console.warn("Token null, tidak bisa download PDF");
      return;
    }

    // Pastikan baseURL sesuai dengan backend
    const res = await axios.get("/transactions/summary/pdf", {
      baseURL: process.env.REACT_APP_API_URL + "/v1/reports", 
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
        "X-API-KEY": apiKey,
      },
      validateStatus: (status) => true, // biar bisa debug semua status
    });

    console.log("Response status:", res.status);
    console.log("Response headers:", res.headers);

    if (res.status === 404) {
      console.error("Endpoint tidak ditemukan! Cek route backend.");
      alert("PDF download failed: Endpoint not found (404).");
      return;
    }

    if (res.status === 403) {
      console.error("Akses ditolak! Cek token/API Key atau role user.");
      alert("PDF download failed: Access denied (403).");
      return;
    }

    if (res.status !== 200) {
      console.error("PDF download gagal:", res.status, res.data);
      alert(`PDF download failed: ${res.status}`);
      return;
    }

    // Kalau berhasil, download PDF
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transaction-report-${new Date().toISOString().slice(0, 10)}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
    console.log("PDF download success!");
  } catch (err) {
    console.error("Unexpected error during PDF download:", err);
    alert("Unexpected error during PDF download, check console.");
  }
};


    // Export to Excel
    const exportToExcel = () => {
        const worksheetData = reportData.map((transaction) => ({
            'Transaction ID': transaction.id,
            'Product Name': transaction.product,
            'Category': transaction.category,
            'Transaction Date': transaction.date,
            'Order Status': transaction.orderStatus,
            'Payment Status': transaction.paymentStatus,
            'Order Type': transaction.orderType,
            'Total Amount': transaction.total,
            'Formatted Total': formatCurrency(transaction.total),
            'Shipping': transaction.shipping,
            'Customer': transaction.customer,
            'Quantity': transaction.quantity,
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Report');

        // Auto size columns
        const wscols = [
            { wch: 15 },  // Transaction ID
            { wch: 25 },  // Product Name
            { wch: 15 },  // Category
            { wch: 15 },  // Date
            { wch: 15 },  // Order Status
            { wch: 15 },  // Payment Status
            { wch: 15 },  // Order Type
            { wch: 15 },  // Total Amount
            { wch: 20 },  // Formatted Total
            { wch: 20 },  // Shipping
            { wch: 20 },  // Customer
            { wch: 10 },  // Quantity
        ];
        worksheet['!cols'] = wscols;

        XLSX.writeFile(workbook, `sales-report-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // Columns untuk react-table
    const columns = React.useMemo(
        () => [
            {
                Header: 'Transaction ID',
                accessor: 'id',
                width: 150,
            },
            {
                Header: 'Product Name',
                accessor: 'product',
                Cell: ({ row }) => (
                    <div className="product-cell">
                        {/* <img
                            src={row.original.image}
                            alt="product"
                            className="product-image"
                            onError={(e) => {
                                e.target.src = 'https://placehold.co/60x60/png';
                            }}
                        /> */}
                        <span>{row.original.product}</span>
                    </div>
                ),
                width: 220,
            },
            {
                Header: 'Transaction Date',
                accessor: 'date',
                width: 140,
            },
            {
                Header: 'Order Status',
                accessor: 'orderStatus',
                Cell: ({ value }) => (
                    <span className={`status-badge ${value.toLowerCase()}`}>
                        {value}
                    </span>
                ),
                width: 120,
            },
            {
                Header: 'Payment Status',
                accessor: 'paymentStatus',
                Cell: ({ value }) => (
                    <span className={`payment-status ${value.toLowerCase()}`}>
                        {value}
                    </span>
                ),
                width: 130,
            },
            {
                Header: 'Category',
                accessor: 'category',
                width: 140,
            },
            {
                Header: 'Order Type',
                accessor: 'orderType',
                width: 110,
            },
            {
                Header: 'Total Transaction',
                accessor: 'total',
                Cell: ({ value }) => formatCurrency(value),
                width: 160,
            },
            {
                Header: 'Shipping By',
                accessor: 'shipping',
                width: 140,
            },
        ],
        []
    );

    const data = useMemo(() => reportData, [reportData]);

    const tableInstance = useTable({
        columns,
        data,
        initialState: {
            sortBy: [{ id: 'date', desc: true }]
        }
    }, useSortBy);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow
    } = tableInstance;

    return (
        <div className="report-page" >
            {isLoading && <div className="loading-message">Loading reports...</div>}
            {error && <div className="error-message">Error: {error}</div>}

            <div className="controls-section">
                {/* Search Box */}
                <div className="search-container">
                    <div className="search-box">
                        <i className="search-icon">🔍</i>
                        <input
                            type="text"
                            placeholder="Search Transaction ID, Product Name or Category"
                            value={searchInput}
                            onChange={handleSearch}
                        />
                    </div>

                    {/* Export Buttons */}
                    <div className="export-buttons">
                        <button className="export-btn pdf-btn" onClick={exportToPDF} disabled={isLoading || reportData.length === 0}>
                            Download PDF
                        </button>
                        <button className="export-btn excel-btn" onClick={exportToExcel} disabled={isLoading || reportData.length === 0}>
                            Export Excel
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="filters-container">
                    <div className="filter-group">
                        <label className="filter-label">All Category</label>
                        <select
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            className="filter-select"
                        >
                            <option value="all">All Categories</option>
                            {getCategories().map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">All Order Type</label>
                        <select
                            value={selectedOrderType}
                            onChange={handleOrderTypeChange}
                            className="filter-select"
                        >
                            <option value="all">All Order Types</option>
                            {getOrderTypes().map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">All Period</label>
                        <div className="date-range">
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => handleDateRangeChange(date, endDate)}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                placeholderText="Start Date"
                                className="date-input"
                                dateFormat="dd/MM/yyyy"
                                isClearable
                            />
                            <span className="date-separator">to</span>
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => handleDateRangeChange(startDate, date)}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                placeholderText="End Date"
                                className="date-input"
                                dateFormat="dd/MM/yyyy"
                                isClearable
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="table-section">
                <div className="table-header">
                    <h3>List Transaction Data</h3>
                    <div className="table-info">
                        Showing {reportData.length} of {totalData} transactions
                    </div>
                </div>

                <div className="table-container">
                    {!isLoading && reportData.length === 0 ? (
                        <div className="no-data-message">
                            <div className="no-data-content">
                                <i className="no-data-icon">📊</i>
                                <p>No transactions found matching your filters</p>
                            </div>
                        </div>
                    ) : (
                        <table {...getTableProps()} className="report-table">
                            <thead>
                                {headerGroups.map(headerGroup => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map(column => (
                                            <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                                <div className="header-content">
                                                    {column.render('Header')}
                                                    <span className="sort-icon">
                                                        {column.isSorted ? (column.isSortedDesc ? ' ↓' : ' ↑') : ' ↕'}
                                                    </span>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                                {rows.map(row => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()}>
                                            {row.cells.map(cell => (
                                                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReportPage;